import { ReactNode, useEffect } from 'react'

import { useLocation, useSearchParams } from 'react-router-dom'

import { observer } from 'mobx-react-lite'

import { promoStore } from 'widgets/avia'

import { BannerMain } from 'features/banner-main'

import { CertificateScreen } from 'entities/sales/components/certificate-screen'
import { isDifficultAviaRoute } from 'entities/ticket/lib/is-difficult-avia-route'
import findTicketStore from 'entities/ticket/models/find-ticket/find-ticket-store'
import { IFindTicketForm } from 'entities/ticket/models/find-ticket/models'
import searchTour, {
  DataForSearchTour,
} from 'entities/tour/models/search-tour/search-tour'
import { UserAuthorityType } from 'entities/user/module'
import { getSettingForApp } from 'entities/user/module/app-settings'
import userStore from 'entities/user/module/user-store'

import {
  refreshOrdersCount,
  resetOrdersCount,
} from 'shared/api/orders-count-query'
// import userStore from 'entities/user/module/user-store'
import { OneHour, OneWeek, SavedFormKeys, restoreFormData } from 'shared/hooks'
import { onPageviewGtagAnalytics } from 'shared/lib/analytics'
import { getLocalStore } from 'shared/lib/local-store'
import AppLoaderGlobal from 'shared/ui/app-loader-global'
import dialogBottomSheetStore from 'shared/ui/dialog-bottom-sheet/model/dialog-bottom-sheet-store'

import {
  getUserByToken,
  getUserData,
  getUserFromInnerToken,
  initLanguageFromQuery,
  isUserInWidgetSearch,
} from './libs'

interface Props {
  children?: ReactNode
}

// const isDev = import.meta.env.VITE_PROJECT_IS_DEV

// const isLocalDev = import.meta.env.VITE_IS_LOCAL_DEV === 'true'

export const UserAvailable = observer(({ children }: Props) => {
  const location = useLocation()

  const [searchParams] = useSearchParams()

  const userToken = searchParams.get('token')

  const innerUserToken = searchParams.get('inner_token')

  const deviceId = searchParams.get('device-id')

  const storedDeviceId = getLocalStore('mTravelDeviceId')

  const effectiveDeviceId = deviceId || storedDeviceId || undefined

  const userAuthorityType = searchParams.get(
    'user-authority',
  ) as UserAuthorityType

  const langParam = searchParams.get('lang')

  // const deviceIdRef = useRef<string | undefined>(deviceId)

  const authUser = async () => {
    if (userAuthorityType) {
      userStore.setUserAuthorityType(userAuthorityType as UserAuthorityType)
    }

    if (effectiveDeviceId) {
      await getSettingForApp(effectiveDeviceId, isUserInWidgetSearch())
    }

    try {
      if (innerUserToken) {
        await getUserFromInnerToken({
          token: innerUserToken,
          userAuthorityType,
        })

        return
      }

      if (userToken && userToken !== 'GUEST') {
        await getUserData({
          token: userToken,
          device_id: effectiveDeviceId,
          userAuthorityType,
        })

        return
      }

      if (getLocalStore('mTravelToken')) {
        await getUserByToken({ userAuthorityType })
      }
    } catch (error) {
      new Error('[Error on auth user]')
    }
  }

  useEffect(() => {
    const initUserAvailable = async () => {
      await authUser()

      if (userStore.account) {
        void refreshOrdersCount()
      } else {
        resetOrdersCount()
      }

      initLanguageFromQuery(langParam)

      restoreFormData<IFindTicketForm>({
        duration: OneHour,
        key: SavedFormKeys.avia.key,
        setter: (data) => {
          if (isDifficultAviaRoute(data.directions)) return

          findTicketStore.setFormFromLocalStorage(data)
        },
      })

      restoreFormData<DataForSearchTour>({
        duration: OneWeek,
        key: SavedFormKeys.tours.key,
        setter: (data) => searchTour.setFormFromLocalStorage(data),
      })

      promoStore.getPromo('avia')
    }

    void initUserAvailable()

    window.addEventListener('popstate', function () {
      if (dialogBottomSheetStore?.child) {
        dialogBottomSheetStore.onHideDialog()
      }
    })
  }, [])

  useEffect(() => {
    if (location?.pathname) {
      onPageviewGtagAnalytics(location?.pathname)
    }
  }, [location?.pathname])

  if (!userStore?.account && userToken) return <AppLoaderGlobal />

  return (
    <BannerMain>
      <CertificateScreen />
      {children}
    </BannerMain>
  )
})
